if (!customElements.get('kivo-home-product-04')) {
  customElements.define(
    'kivo-home-product-04',
    class KivoHomeProduct04 extends HTMLElement {
      connectedCallback() {
        this.carousel = this.querySelector('[data-kivo-home-product-04-carousel]');
        this.count = this.querySelector('[data-kivo-home-product-04-count]');
        this.reelProgress = this.querySelector('[data-kivo-home-product-04-reel-progress]');
        this.reelProgressTrack = this.querySelector('[data-kivo-home-product-04-reel-progress-track]');

        if (!this.carousel) return;

        this.originalItems = Array.from(this.carousel.querySelectorAll('[data-kivo-home-product-04-item]')).sort(
          (first, second) => Number(first.dataset.originalIndex) - Number(second.dataset.originalIndex)
        );
        this.itemCount = this.originalItems.length;
        this.totalCount = Math.max(Number(this.dataset.totalCount) || 0, this.originalItems.length);
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.videoHandlers = new Map();
        this.scrollFrame = null;
        this.resizeFrame = null;
        this.programmaticScrollTarget = null;
        this.programmaticScrollTimer = null;

        if (this.reelProgressTrack) {
          this.reelProgressTrack.setAttribute('aria-valuemax', String(this.itemCount));
        }

        this.handleClick = this.handleClick.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleCarouselPointerDown = this.handleCarouselPointerDown.bind(this);
        this.handleCarouselScroll = this.handleCarouselScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
        this.carousel.addEventListener('pointerdown', this.handleCarouselPointerDown, { passive: true });
        this.carousel.addEventListener('scroll', this.handleCarouselScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });
        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener('change', this.handleViewportChange);
        } else {
          this.mobileMediaQuery.addListener(this.handleViewportChange);
        }

        this.originalItems.forEach((item) => this.bindVideo(item));
        this.activate(this.carousel.querySelector('.is-active') || this.originalItems[0], true);
        this.syncCarouselLayout(true);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.carousel?.removeEventListener('pointerdown', this.handleCarouselPointerDown);
        this.carousel?.removeEventListener('scroll', this.handleCarouselScroll);
        window.removeEventListener('resize', this.handleResize);

        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
        this.clearProgrammaticScroll();

        if (this.mobileMediaQuery) {
          if (this.mobileMediaQuery.removeEventListener) {
            this.mobileMediaQuery.removeEventListener('change', this.handleViewportChange);
          } else {
            this.mobileMediaQuery.removeListener(this.handleViewportChange);
          }
        }

        this.videoHandlers?.forEach((handlers, video) => {
          Object.entries(handlers).forEach(([eventName, handler]) => video.removeEventListener(eventName, handler));
          video.pause();
        });
        this.videoHandlers?.clear();
      }

      handleClick(event) {
        const playButton = event.target.closest('[data-kivo-home-product-04-play]');
        if (playButton) {
          this.togglePlayback(playButton.closest('[data-kivo-home-product-04-item]'));
          return;
        }

        const selectButton = event.target.closest('[data-kivo-home-product-04-select]');
        if (selectButton) {
          this.activate(selectButton.closest('[data-kivo-home-product-04-item]'));
          return;
        }

        if (event.target.closest('[data-kivo-home-product-04-prev]')) {
          this.move(-1);
          return;
        }

        if (event.target.closest('[data-kivo-home-product-04-next]')) {
          this.move(1);
        }
      }

      handleBlockSelect(event) {
        const item = event.target.closest('[data-kivo-home-product-04-item]');
        if (item) this.activate(item);
      }

      handleCarouselPointerDown() {
        this.clearProgrammaticScroll();
      }

      handleCarouselScroll() {
        if (!this.mobileMediaQuery.matches || this.scrollFrame) return;

        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;

          if (this.programmaticScrollTarget) {
            if (Math.abs(this.itemOffset(this.programmaticScrollTarget) - this.carousel.scrollLeft) <= 1) {
              this.clearProgrammaticScroll();
            }
            return;
          }

          const nextItem = this.closestMobileItem();
          if (nextItem && !nextItem.classList.contains('is-active')) this.activate(nextItem, false, false);
        });
      }

      handleResize() {
        if (!this.mobileMediaQuery.matches) return;
        if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);

        this.resizeFrame = window.requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.syncCarouselLayout(true);
        });
      }

      handleViewportChange() {
        this.originalItems.forEach((item) => {
          this.resetItemVideo(item);
          const video = item.querySelector('[data-kivo-home-product-04-video]');
          if (video) video.load();
          this.updatePlayAvailability(item);
        });
        this.syncCarouselLayout(true);
      }

      bindVideo(item) {
        const video = item.querySelector('[data-kivo-home-product-04-video]');
        if (!video) return;

        const handlers = {
          loadedmetadata: () => this.updateVideoProgress(item),
          timeupdate: () => this.updateVideoProgress(item),
          waiting: () => {
            if (!video.paused && !video.ended) {
              item.classList.add('is-buffering');
            }
          },
          canplay: () => {
            if (!video.paused && !video.ended && item.classList.contains('is-playing')) {
              item.classList.remove('is-buffering');
            }
          },
          playing: () => {
            item.classList.remove('is-buffering');
            this.setPlayingState(item, true);
          },
          pause: () => {
            item.classList.remove('is-buffering');
            this.setPlayingState(item, false);
          },
          ended: () => {
            item.classList.remove('is-buffering');
            this.setPlayingState(item, false);
            this.updateVideoProgress(item, 100);
          },
          error: () => {
            item.classList.remove('is-buffering');
            this.updatePlayAvailability(item);
          },
        };

        Object.entries(handlers).forEach(([eventName, handler]) => video.addEventListener(eventName, handler));
        this.videoHandlers.set(video, handlers);
      }

      move(direction) {
        const active = this.carousel.querySelector('.is-active');
        const currentIndex = this.originalItems.indexOf(active);
        if (currentIndex < 0 || this.originalItems.length < 2) return;

        const nextIndex = (currentIndex + direction + this.originalItems.length) % this.originalItems.length;
        this.activate(this.originalItems[nextIndex]);
      }

      activate(nextItem, initial = false, syncMobileScroll = true) {
        if (!nextItem) return;

        const previousItem = this.carousel.querySelector('.is-active');
        if (!initial && previousItem === nextItem) {
          if (syncMobileScroll && this.mobileMediaQuery.matches) this.scrollToMobileItem(nextItem);
          return;
        }

        if (previousItem && previousItem !== nextItem) this.resetItemVideo(previousItem);

        if (!initial && previousItem && !this.mobileMediaQuery.matches) {
          this.carousel.insertBefore(nextItem, this.carousel.firstElementChild);
          this.carousel.appendChild(previousItem);
        }

        Array.from(this.carousel.querySelectorAll('[data-kivo-home-product-04-item]')).forEach((item) => {
          const isActive = item === nextItem;
          const selectButton = item.querySelector('[data-kivo-home-product-04-select]');

          item.classList.toggle('is-active', isActive);
          if (isActive) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }

          if (selectButton) selectButton.tabIndex = isActive ? -1 : 0;
          this.updatePlayAvailability(item);
        });

        this.updateStatus(nextItem);
        this.updateVideoProgress(nextItem);

        if (this.mobileMediaQuery.matches && syncMobileScroll) {
          this.scrollToMobileItem(nextItem, initial);
        }

        if (!initial && !this.mobileMediaQuery.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          nextItem.classList.remove('is-entering');
          void nextItem.offsetWidth;
          nextItem.classList.add('is-entering');
          window.setTimeout(() => nextItem.classList.remove('is-entering'), 380);
        }
      }

      syncCarouselLayout(instant = false) {
        const activeItem = this.carousel.querySelector('.is-active') || this.originalItems[0];
        if (!activeItem) return;

        if (this.mobileMediaQuery.matches) {
          this.originalItems.forEach((item) => this.carousel.appendChild(item));
          this.scrollToMobileItem(activeItem, instant);
          return;
        }

        this.clearProgrammaticScroll();
        if (this.carousel.firstElementChild !== activeItem) {
          this.carousel.insertBefore(activeItem, this.carousel.firstElementChild);
        }
        this.carousel.scrollLeft = 0;
      }

      itemOffset(item) {
        const firstItem = this.originalItems[0];
        if (!item || !firstItem) return 0;
        return item.offsetLeft - firstItem.offsetLeft;
      }

      closestMobileItem() {
        return this.originalItems.reduce(
          (result, item) => {
            const distance = Math.abs(this.itemOffset(item) - this.carousel.scrollLeft);
            return distance < result.distance ? { item, distance } : result;
          },
          { item: null, distance: Number.POSITIVE_INFINITY }
        ).item;
      }

      scrollToMobileItem(item, instant = false) {
        if (!this.mobileMediaQuery.matches || !item) return;

        const targetOffset = this.itemOffset(item);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const shouldAnimate = !instant && !reducedMotion;

        this.clearProgrammaticScroll();
        this.programmaticScrollTarget = item;
        this.carousel.scrollTo({ left: targetOffset, behavior: shouldAnimate ? 'smooth' : 'auto' });

        if (shouldAnimate) {
          this.programmaticScrollTimer = window.setTimeout(() => {
            this.clearProgrammaticScroll();
            const closestItem = this.closestMobileItem();
            if (closestItem && !closestItem.classList.contains('is-active')) this.activate(closestItem, false, false);
          }, 700);
        } else {
          this.programmaticScrollTimer = window.setTimeout(() => this.clearProgrammaticScroll(), 0);
        }
      }

      clearProgrammaticScroll() {
        this.programmaticScrollTarget = null;
        if (this.programmaticScrollTimer) window.clearTimeout(this.programmaticScrollTimer);
        this.programmaticScrollTimer = null;
      }

      togglePlayback(item) {
        if (!item || !this.hasVideoForViewport(item)) return;

        if (!item.classList.contains('is-active')) this.activate(item);

        const video = item.querySelector('[data-kivo-home-product-04-video]');
        if (!video) return;

        if (!video.paused && !video.ended) {
          video.pause();
          return;
        }

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        this.originalItems.forEach((otherItem) => {
          if (otherItem !== item) this.resetItemVideo(otherItem);
        });

        if (video.ended) video.currentTime = 0;
        item.classList.add('is-buffering');
        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            item.classList.remove('is-buffering', 'has-video-frame');
            this.setPlayingState(item, false);
          });
        }
      }

      hasVideoForViewport(item) {
        const datasetKey = this.mobileMediaQuery.matches ? 'hasMobileVideo' : 'hasDesktopVideo';
        return item.dataset[datasetKey] === 'true';
      }

      updatePlayAvailability(item) {
        const playButton = item.querySelector('[data-kivo-home-product-04-play]');
        if (!playButton) return;

        const hasVideo = this.hasVideoForViewport(item);
        playButton.disabled = !hasVideo;
        playButton.setAttribute('aria-disabled', hasVideo ? 'false' : 'true');
        playButton.tabIndex = hasVideo ? 0 : -1;
      }

      setPlayingState(item, isPlaying) {
        const playButton = item.querySelector('[data-kivo-home-product-04-play]');

        item.classList.toggle('is-playing', isPlaying);
        if (isPlaying) {
          item.classList.remove('is-buffering');
          item.classList.add('has-video-frame');
        }

        if (playButton) {
          playButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
          playButton.setAttribute('aria-label', isPlaying ? playButton.dataset.pauseLabel : playButton.dataset.playLabel);
        }
      }

      resetItemVideo(item) {
        const video = item.querySelector('[data-kivo-home-product-04-video]');
        if (video) {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (error) {
            // The media source has not loaded yet, so there is no playback position to reset.
          }
        }

        item.classList.remove('is-playing', 'is-buffering', 'has-video-frame');
        this.setPlayingState(item, false);
        this.updateVideoProgress(item, 0, true);
      }

      updateVideoProgress(item, forcedPercent = null, resetDuration = false) {
        const video = item.querySelector('[data-kivo-home-product-04-video]');
        const progress = item.querySelector('[data-kivo-home-product-04-video-progress]');
        const currentTime = item.querySelector('[data-kivo-home-product-04-current-time]');
        const duration = item.querySelector('[data-kivo-home-product-04-duration]');
        if (!progress || !currentTime || !duration) return;

        const videoDuration = video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
        const videoCurrentTime = video && Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const percent =
          forcedPercent === null && videoDuration
            ? Math.min(100, Math.max(0, (videoCurrentTime / videoDuration) * 100))
            : Math.min(100, Math.max(0, forcedPercent || 0));

        item.style.setProperty('--kivo-home-product-04-video-progress', `${percent}%`);
        progress.setAttribute('aria-valuenow', String(Math.round(percent)));
        currentTime.textContent = this.formatTime(resetDuration ? 0 : videoCurrentTime);

        if (videoDuration && !resetDuration) {
          duration.textContent = this.formatTime(videoDuration);
        } else if (resetDuration) {
          duration.textContent = duration.dataset.fallbackDuration || '--:--';
        }
      }

      formatTime(value) {
        if (!Number.isFinite(value) || value < 0) return '00:00';

        const totalSeconds = Math.floor(value);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }

      updateStatus(activeItem) {
        const itemNumber = Number(activeItem.dataset.originalIndex) + 1;
        const displayNumber = String(itemNumber).padStart(2, '0');

        if (this.count) this.count.textContent = `${displayNumber} / ${this.totalCount}`;
        if (this.reelProgress) {
          this.style.setProperty(
            '--kivo-home-product-04-reel-progress',
            `${Math.min((itemNumber / this.itemCount) * 100, 100)}%`
          );
        }
        if (this.reelProgressTrack) {
          this.reelProgressTrack.setAttribute('aria-valuenow', String(itemNumber));
        }
      }
    }
  );
}
